<template>
  <div class="flow-gauge" :style="{ width: size + 'px', height: (size * 0.62) + 'px' }">
    <svg viewBox="0 0 100 62" width="100%" height="100%">
      <path v-for="(zone, i) in zones" :key="i" class="flow-gauge-zone" :d="zone.path" :stroke="zone.color" />
      <line
        class="flow-gauge-needle"
        x1="50"
        y1="50"
        x2="50"
        y2="16"
        :stroke="needleColor"
        :transform="`rotate(${needleAngle} 50 50)`"
      />
      <circle class="flow-gauge-hub" cx="50" cy="50" r="4" :fill="needleColor" />
    </svg>
  </div>
</template>

<script>
const RED = "#c13745";
const GREEN = "#3d9161";
const YELLOW = "#edad49";
const GRAY = "#9e9ea0";

function pointAt(deg, cx, cy, r) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
}

// Arco de -90° (0%) a +90° (100%), medido a partir do topo (0°) como no PivotDial.
function arcPath(fromPct, toPct) {
  const cx = 50;
  const cy = 50;
  const r = 34;
  const fromDeg = -90 + fromPct * 180;
  const toDeg = -90 + toPct * 180;
  const start = pointAt(fromDeg, cx, cy, r);
  const end = pointAt(toDeg, cx, cy, r);
  const largeArc = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

export default {
  name: "flow-gauge",
  props: {
    flow: { type: Number, default: 0 },
    minFlow: { type: Number, default: 0 },
    maxFlow: { type: Number, default: 0 },
    size: { type: Number, default: 48 },
    // Atuador desligado: vazão 0 é esperada, não uma falha — o gauge fica neutro
    // em vez de acender a zona vermelha de "abaixo do mínimo".
    active: { type: Boolean, default: true },
  },
  computed: {
    // Referência de escala do velocímetro: 25% de folga acima do máximo configurado
    // para o ponteiro não "bater" no fim do arco assim que a vazão atinge o teto.
    scaleMax() {
      if (this.maxFlow > 0) return this.maxFlow * 1.25;
      return Math.max(this.flow, this.minFlow, 1) * 1.5;
    },
    minPct() {
      return this.clamp((this.minFlow || 0) / this.scaleMax);
    },
    maxPct() {
      if (!(this.maxFlow > 0)) return null;
      return this.clamp(this.maxFlow / this.scaleMax);
    },
    percent() {
      return this.clamp(this.flow / this.scaleMax);
    },
    needleAngle() {
      return -90 + this.percent * 180;
    },
    // Sem MAX_FLOW configurado, ou com o atuador desligado, não há faixa de
    // status a avaliar (mesma regra usada em setBG()/selectInfoStatus()), então
    // o gauge fica neutro em vez de acender vermelho/amarelo.
    zones() {
      if (this.maxPct === null || !this.active) {
        return [{ path: arcPath(0, 1), color: GRAY }];
      }
      const zones = [];
      if (this.minPct > 0) zones.push({ path: arcPath(0, this.minPct), color: RED });
      zones.push({ path: arcPath(this.minPct, this.maxPct), color: GREEN });
      if (this.maxPct < 1) zones.push({ path: arcPath(this.maxPct, 1), color: YELLOW });
      return zones;
    },
    // Mesma semântica de zona usada no restante do card (ABAIXO/OK/ACIMA).
    needleColor() {
      if (this.maxPct === null || !this.active) return GRAY;
      if (this.percent < this.minPct) return RED;
      if (this.percent > this.maxPct) return YELLOW;
      return GREEN;
    },
  },
  methods: {
    clamp(value) {
      return Math.min(1, Math.max(0, value || 0));
    },
  },
};
</script>

<style scoped>
.flow-gauge {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}
.flow-gauge-zone {
  fill: none;
  stroke-width: 7;
  stroke-linecap: round;
}
.flow-gauge-needle {
  stroke-width: 3;
  stroke-linecap: round;
  transition: transform 0.4s ease;
}
.flow-gauge-hub {
  stroke: #fff;
  stroke-width: 1.2;
}
</style>
